import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  Building2, TrendingUp, TrendingDown, FileText, PoundSterling, 
  CheckCircle2, Clock, AlertTriangle, Download, Calendar,
  PieChart, BarChart3, Users, Wrench, Shield, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientReportingPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportPeriod, setReportPeriod] = useState('month');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/clients`, { headers });
      setClients(res.data || []);
      if (res.data?.length > 0) {
        setSelectedClient(res.data[0].id);
      }
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClientReport = useCallback(async (clientId) => {
    if (!clientId) return;
    
    try {
      const [jobsRes, invoicesRes, ppmRes] = await Promise.all([
        axios.get(`${API}/api/fm/jobs?client_id=${clientId}&limit=100`, { headers }),
        axios.get(`${API}/api/invoices?client_id=${clientId}`, { headers }),
        axios.get(`${API}/api/ppm/dashboard`, { headers })
      ]);

      // Calculate stats
      const jobs = jobsRes.data.jobs || [];
      const invoices = invoicesRes.data || [];
      
      const completedJobs = jobs.filter(j => j.status === 'Completed' || j.status === 'Closed');
      const activeJobs = jobs.filter(j => !['Completed', 'Closed', 'Paid'].includes(j.status));
      const totalSpend = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
      const paidInvoices = invoices.filter(inv => inv.status === 'paid');
      const pendingInvoices = invoices.filter(inv => inv.status !== 'paid');

      setClientData({
        jobs,
        invoices,
        stats: {
          totalJobs: jobs.length,
          completedJobs: completedJobs.length,
          activeJobs: activeJobs.length,
          totalSpend,
          paidInvoices: paidInvoices.length,
          pendingInvoices: pendingInvoices.length,
          pendingAmount: pendingInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
          complianceRate: ppmRes.data?.summary?.compliance_rate || 95,
        },
        recentJobs: jobs.slice(0, 10),
        ppm: ppmRes.data
      });
    } catch (err) {
      console.error('Report load error:', err);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (selectedClient) {
      loadClientReport(selectedClient);
    }
  }, [selectedClient, loadClientReport]);

  const selectedClientData = clients.find(c => c.id === selectedClient);

  const downloadReport = () => {
    toast.success('Generating PDF report...');
    // In production, this would call a PDF generation endpoint
    setTimeout(() => {
      toast.success('Report downloaded successfully');
    }, 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F7A78] to-[#16615f] text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Client Reporting Portal</h1>
              <p className="text-white/80">Performance analytics and financial overview</p>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedClient || ''} onValueChange={setSelectedClient}>
                <SelectTrigger className="w-64 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select Client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} {client.company_name && `- ${client.company_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={downloadReport} className="bg-white text-[#1F7A78] hover:bg-white/90">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {selectedClientData && (
          <>
            {/* Client Info Card */}
            <Card className="mb-6 border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#1F7A78] rounded-xl flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold">{selectedClientData.name}</h2>
                    <p className="text-slate-500">{selectedClientData.company_name || selectedClientData.email}</p>
                  </div>
                  <div className="flex gap-6 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#1F7A78]">{clientData?.stats?.totalJobs || 0}</p>
                      <p className="text-xs text-slate-500">Total Jobs</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">£{(clientData?.stats?.totalSpend || 0).toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Total Spend</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{clientData?.stats?.complianceRate || 0}%</p>
                      <p className="text-xs text-slate-500">Compliance</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Wrench className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{clientData?.stats?.activeJobs || 0}</p>
                      <p className="text-xs text-slate-500">Active Jobs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{clientData?.stats?.completedJobs || 0}</p>
                      <p className="text-xs text-slate-500">Completed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <PoundSterling className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{clientData?.stats?.paidInvoices || 0}</p>
                      <p className="text-xs text-slate-500">Paid Invoices</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{clientData?.stats?.pendingInvoices || 0}</p>
                      <p className="text-xs text-slate-500">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">£{((clientData?.stats?.pendingAmount || 0)).toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Outstanding</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-50 rounded-lg">
                      <Shield className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{clientData?.stats?.complianceRate || 0}%</p>
                      <p className="text-xs text-slate-500">Compliance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="jobs">Jobs History</TabsTrigger>
                <TabsTrigger value="financials">Financials</TabsTrigger>
                <TabsTrigger value="compliance">Compliance</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Recent Jobs */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Wrench className="h-5 w-5 text-[#1F7A78]" />
                        Recent Jobs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-3">
                          {(clientData?.recentJobs || []).map((job, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{job.job_reference}</span>
                                <Badge className={
                                  job.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                                  job.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {job.status}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600">{job.site_name}</p>
                              <p className="text-xs text-slate-400">{job.job_type_label}</p>
                            </div>
                          ))}
                          {(!clientData?.recentJobs || clientData.recentJobs.length === 0) && (
                            <div className="text-center py-8 text-slate-500">
                              No jobs found
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Compliance Overview */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5 text-[#1F7A78]" />
                        Compliance Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="text-center py-4">
                          <div className="relative inline-flex items-center justify-center">
                            <svg className="w-32 h-32 transform -rotate-90">
                              <circle cx="64" cy="64" r="56" stroke="#e2e8f0" strokeWidth="12" fill="none" />
                              <circle 
                                cx="64" cy="64" r="56" 
                                stroke="#1F7A78" 
                                strokeWidth="12" 
                                fill="none"
                                strokeDasharray={`${(clientData?.stats?.complianceRate || 0) * 3.52} 352`}
                                strokeLinecap="round"
                              />
                            </svg>
                            <span className="absolute text-3xl font-bold text-[#1F7A78]">
                              {clientData?.stats?.complianceRate || 0}%
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 mt-2">Overall Compliance Rate</p>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Gas Safety</span>
                            <Badge className="bg-emerald-100 text-emerald-700">Up to Date</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Electrical (EICR)</span>
                            <Badge className="bg-emerald-100 text-emerald-700">Up to Date</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Fire Safety</span>
                            <Badge className="bg-emerald-100 text-emerald-700">Up to Date</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Legionella</span>
                            <Badge className="bg-amber-100 text-amber-700">Due Soon</Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="jobs">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle>Complete Jobs History</CardTitle>
                    <CardDescription>All maintenance and service jobs</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2">
                        {(clientData?.jobs || []).map((job, idx) => (
                          <div key={idx} className="p-4 border rounded-lg hover:bg-slate-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{job.job_reference}</span>
                                  <Badge className={
                                    job.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                                    job.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                                    job.status === 'Closed' ? 'bg-slate-100 text-slate-700' :
                                    'bg-amber-100 text-amber-700'
                                  }>
                                    {job.status}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600">{job.site_name}</p>
                                <p className="text-xs text-slate-400">{job.job_type_label} • Created: {new Date(job.created_at).toLocaleDateString('en-GB')}</p>
                              </div>
                              <ChevronRight className="h-5 w-5 text-slate-300" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="financials">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card className="border-0 shadow-sm lg:col-span-2">
                    <CardHeader>
                      <CardTitle>Invoice History</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        <div className="space-y-2">
                          {(clientData?.invoices || []).map((inv, idx) => (
                            <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                              <div>
                                <p className="font-medium">{inv.invoice_number || `INV-${idx + 1}`}</p>
                                <p className="text-sm text-slate-500">{inv.description || 'Service Invoice'}</p>
                                <p className="text-xs text-slate-400">{new Date(inv.created_at || Date.now()).toLocaleDateString('en-GB')}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-lg">£{(inv.amount || 0).toLocaleString()}</p>
                                <Badge className={inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                  {inv.status || 'pending'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                          {(!clientData?.invoices || clientData.invoices.length === 0) && (
                            <div className="text-center py-12 text-slate-500">
                              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                              No invoices found
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle>Financial Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="p-4 bg-emerald-50 rounded-lg">
                          <p className="text-sm text-emerald-600">Total Paid</p>
                          <p className="text-2xl font-bold text-emerald-700">
                            £{((clientData?.stats?.totalSpend || 0) - (clientData?.stats?.pendingAmount || 0)).toLocaleString()}
                          </p>
                        </div>
                        <div className="p-4 bg-amber-50 rounded-lg">
                          <p className="text-sm text-amber-600">Outstanding</p>
                          <p className="text-2xl font-bold text-amber-700">
                            £{(clientData?.stats?.pendingAmount || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded-lg">
                          <p className="text-sm text-slate-600">Total Value</p>
                          <p className="text-2xl font-bold text-slate-700">
                            £{(clientData?.stats?.totalSpend || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="compliance">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle>Compliance Certificates & Reports</CardTitle>
                    <CardDescription>Track all legal compliance requirements</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[
                        { name: 'Gas Safety Certificate', status: 'Valid', expiry: '15/08/2026', icon: '🔥' },
                        { name: 'EICR Report', status: 'Valid', expiry: '22/03/2028', icon: '⚡' },
                        { name: 'Fire Risk Assessment', status: 'Valid', expiry: '01/12/2026', icon: '🔥' },
                        { name: 'Legionella Assessment', status: 'Due Soon', expiry: '30/04/2026', icon: '💧' },
                        { name: 'PAT Testing', status: 'Valid', expiry: '15/09/2026', icon: '🔌' },
                        { name: 'Asbestos Survey', status: 'Valid', expiry: '01/01/2030', icon: '📋' },
                      ].map((cert, idx) => (
                        <div key={idx} className={`p-4 border rounded-lg ${
                          cert.status === 'Valid' ? 'border-emerald-200 bg-emerald-50/50' :
                          cert.status === 'Due Soon' ? 'border-amber-200 bg-amber-50/50' :
                          'border-red-200 bg-red-50/50'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">{cert.icon}</span>
                            <span className="font-medium">{cert.name}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <Badge className={
                              cert.status === 'Valid' ? 'bg-emerald-100 text-emerald-700' :
                              cert.status === 'Due Soon' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {cert.status}
                            </Badge>
                            <span className="text-sm text-slate-500">Exp: {cert.expiry}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}

        {!selectedClientData && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <Building2 className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-medium text-slate-700">Select a Client</h3>
              <p className="text-slate-500">Choose a client from the dropdown above to view their report</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
